import {
    Input,
    Component,
    Output,
    EventEmitter,
    OnChanges
} from '@angular/core';
import { List } from 'immutable';
import { SearchState } from '../core-data-model/SearchState.model';
import { Util } from '../core-utilities/Util';
import { TranslationService } from '../translations/Translation.service';
import { PagerUtilities } from './Pager.utility';

@Component({
    selector: 'clientside-pi-pager',
    template: `
        <div class="pi-pagination">
            @if (util.hasValue(dataArray && this.length())) {
                <div
                    class="pi-pagination_resultSizeInformation"
                    data-testing="pagingAndTotalCountInformation"
                >
                    {{ resultSizeInformation() }}
                </div>
            }
            <div>
                <span
                    class="pi-pagination_pageNumber"
                    [ngClass]="{
                        'pi-pagination_pageNumber--clickableNumber':
                            1 < currentPageNumber
                    }"
                    (click)="goToPage(currentPageNumber - 1)"
                >
                    {{ TR.key('text.common-terms.prev') }}
                </span>

                @for (
                    pageNumber of pagerUtil.pagingNumbersDisplay(
                        currentPageNumber,
                        getMaxPage
                    );
                    track pageNumber
                ) {
                    <span
                        class="pi-pagination_pageNumber"
                        [ngClass]="{
                            'pi-pagination_pageNumber--clickableNumber':
                                isClickablePage(pageNumber),
                            'pi-pagination_pageNumber--currentPage':
                                isCurrentPage(pageNumber)
                        }"
                        (click)="goToPage(pageNumber)"
                    >
                        @if (pageNumber === ellipsisValue) {
                            <span>...</span>
                        } @else {
                            {{ pageNumber }}
                        }
                    </span>
                }

                <span
                    class="pi-pagination_pageNumber"
                    [ngClass]="{
                        'pi-pagination_pageNumber--clickableNumber':
                            !isLastPage()
                    }"
                    (click)="goToPage(currentPageNumber + 1)"
                    data-testing="next-page-button"
                >
                    {{ TR.key('text.common-terms.next') }}
                </span>
            </div>

            @if (showResultsSizePicker) {
                <pi-pager-results-size-picker
                    [searchState]="searchState"
                    (onResultsSizeChange)="updateResultsPerPage()"
                >
                </pi-pager-results-size-picker>
            }
        </div>
    `,
    standalone: false
})
export class ClientsidePiPagerComponent implements OnChanges {
    // this component uses already fetched data (hence the name)
    // and does not have filtering feature
    // as opposed to TablePager which uses OData query and has filtering feature

    // Support an array or immutable list
    @Input()
    dataArray: List<any>;
    // Either the consumer passes in a fixed results per page number
    @Input()
    fixedResultsPerPage: number;
    // OR they pass in a search state object that will allow variable changes of the page size. We will also bind to the search state for current page
    @Input()
    searchState: SearchState = new SearchState();

    @Input()
    showResultsSizePicker = false;

    @Input()
    resetPageNumber: boolean;

    @Output()
    startAndEndIndexes = new EventEmitter<number[]>();

    get currentPageNumber() {
        return this.searchState.pageState.currentPageNumber;
    }
    set currentPageNumber(page: number) {
        this.searchState.pageState.currentPageNumber = page;
    }

    // To display the "..."
    ellipsisValue = -1;

    constructor(
        public TR: TranslationService,
        public util: Util,
        public pagerUtil: PagerUtilities
    ) {}

    ngOnChanges() {
        if (this.resetPageNumber) {
            this.currentPageNumber = 1;
            this.goToPage(this.currentPageNumber);
        }
    }

    get resultsPerPage() {
        if (this.fixedResultsPerPage !== undefined) {
            return this.fixedResultsPerPage;
        }
        return this.searchState.pageState.resultsPerPage;
    }

    updateResultsPerPage() {
        // Refresh the contents with new size
        this.goToPage(this.currentPageNumber);
    }

    get getMaxPage() {
        return Math.ceil(this.length() / this.resultsPerPage);
    }

    length() {
        if (this.dataArray instanceof Array) {
            return this.dataArray.length;
        }
        return this.dataArray.size;
    }

    isClickablePage(pageNumber: number): boolean {
        return (
            pageNumber !== this.currentPageNumber &&
            pageNumber !== this.ellipsisValue
        );
    }

    isCurrentPage(pageNumber: number): boolean {
        return pageNumber === this.currentPageNumber;
    }

    isLastPage() {
        return this.getMaxPage === this.currentPageNumber;
    }

    resultSizeInformation() {
        if (this.length() === 0) {
            return this.TR.key('tablePager.noResultsFound');
        }

        let currentResultRange = `${
            (this.currentPageNumber - 1) * this.resultsPerPage + 1
        }-`;
        if (this.currentPageNumber * this.resultsPerPage > this.length()) {
            currentResultRange += this.length();
        } else {
            currentResultRange += `${
                this.currentPageNumber * this.resultsPerPage
            }`;
        }

        return this.util.strings.formatString(
            this.TR.key('tablePager.resultSize'),
            currentResultRange,
            this.length()
        );
    }

    goToPage(pageNumber: number) {
        // page number is 1-indexed and not 0-indexed

        if (
            // Don't go past bounds
            pageNumber < 1 ||
            pageNumber > this.getMaxPage
        ) {
            return;
        }

        const startIndex = this.resultsPerPage * (pageNumber - 1);
        const endIndex = startIndex + this.resultsPerPage;
        this.currentPageNumber = pageNumber;
        this.startAndEndIndexes.emit([startIndex, endIndex]);
    }
}
